import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getQualifiedToValidate, advanceAppointment, contractTypeOf, type RdvRow } from '../../lib/refonteApi';
import RdvTabs from '../../components/refonte/RdvTabs';
import SourceBadge from '../../components/refonte/SourceBadge';
import StatusBadge from '../../components/refonte/StatusBadge';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

// RDV QUALIFIÉS → À VALIDER (CSM/direction). La boucle de facturation hybride n'avait pas
// d'écran : 1 seul « validated » en 30 jours (audit UX 09/10). Un RDV validé = facturable.
// Le moteur advance_appointment garde les rôles (csm/admin/super_admin) et le pricing hybride.
const RdvAValiderPage: React.FC = () => {
  const [rows, setRows] = useState<RdvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await getQualifiedToValidate()); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (rdv: RdvRow, to: 'validated' | 'held') => {
    setBusy(rdv.id); setErr(null);
    try { await advanceAppointment(rdv.id, to); await load(); }
    catch (e: any) { setErr(e.message); }
    setBusy(null);
  };

  const hybrides = rows.filter(r => contractTypeOf(r) === 'variable');
  const autres = rows.filter(r => contractTypeOf(r) !== 'variable');

  const Line: React.FC<{ r: RdvRow; billable: boolean }> = ({ r, billable }) => (
    <div className="py-2.5 flex items-center gap-3 flex-wrap">
      <span className="font-mono text-[11px] text-faint w-20 shrink-0">{r.date ?? '—'}</span>
      <Link to={`/app/portail/rdv/${r.id}?c=${(r as any).client_id ?? ''}`} className="font-semibold text-[13px] hover:text-hook-rose" title="Ouvrir la fiche du RDV (résumé, enregistrement) avant de décider">{r.prospect_name || 'Prospect'}</Link>
      <span className="text-dim text-[12px]">{(r as any).clients?.name ?? ''}{r.company_name ? ` · ${r.company_name}` : ''}</span>
      <SourceBadge source={(r as any).source} compact />
      <StatusBadge status={r.lifecycle_status ?? null} />
      <span className="ml-auto flex items-center gap-2">
        {billable && (
          <button disabled={busy === r.id} onClick={() => act(r, 'validated')}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-hook-rose text-white hover:opacity-90 disabled:opacity-40">
            ✓ Valider (facturable)
          </button>
        )}
        <button disabled={busy === r.id} onClick={() => act(r, 'held')}
          className="text-[12px] px-3 py-1.5 rounded-lg border border-line-2 text-dim hover:border-ink disabled:opacity-40"
          title="Repasse le RDV en honoré (retire la qualification)">↩ Repasser honoré</button>
      </span>
    </div>
  );

  return (
    <div>
      <h1 className="text-[21px] font-bold tracking-tight mb-1">Rendez-vous</h1>
      <p className="text-dim text-[13px] mb-4">Qualifiés en attente de validation — un RDV validé entre en facturation (contrats hybrides).</p>
      <RdvTabs showValidate />
      {err && <div className="mb-4 text-[12px] text-st-bad bg-st-bad/10 rounded px-3 py-2">{err}</div>}
      {loading ? <SkeletonRows count={5} /> : rows.length === 0 ? (
        <EmptyState title="Aucun RDV qualifié en attente" hint="Les RDV passés « qualifié » par le client ou l'équipe arrivent ici pour validation." />
      ) : (
        <>
          {hybrides.length > 0 && (
            <div className="bg-panel border border-line rounded-card p-5 mb-4">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-2">Contrats hybrides — à valider pour facturer ({hybrides.length})</h2>
              <div className="divide-y divide-line">{hybrides.map(r => <Line key={r.id} r={r} billable />)}</div>
            </div>
          )}
          {autres.length > 0 && (
            <div className="bg-panel border border-line rounded-card p-5">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-2">Autres contrats — qualifiés (pas de facturation au RDV) ({autres.length})</h2>
              <div className="divide-y divide-line">{autres.map(r => <Line key={r.id} r={r} billable={false} />)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
export default RdvAValiderPage;
